import React, { useState, useRef } from 'react';
import SEO from './SEO';

type ImageFormat = 'png' | 'jpeg' | 'webp' | 'bmp';

interface ConvertedImage {
  name: string;
  url: string;
  size: number;
  format: ImageFormat;
}

const ImageConverter: React.FC = () => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [targetFormat, setTargetFormat] = useState<ImageFormat>('png');
  const [quality, setQuality] = useState(0.9);
  const [convertedImages, setConvertedImages] = useState<ConvertedImage[]>([]);
  const [isConverting, setIsConverting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const supportedFormats = ['png', 'jpeg', 'jpg', 'webp', 'bmp', 'gif'];

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const convertImage = (file: File, format: ImageFormat, quality: number): Promise<ConvertedImage> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        
        if (ctx) {
          // 如果转换为JPEG，先填充白色背景
          if (format === 'jpeg') {
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }
          
          ctx.drawImage(img, 0, 0);
          
          const mimeType = format === 'jpeg' ? 'image/jpeg' : `image/${format}`;
          const dataUrl = canvas.toDataURL(mimeType, quality);
          
          // 计算文件大小
          const base64Data = dataUrl.split(',')[1];
          const size = Math.round((base64Data.length * 3) / 4);
          
          const fileName = file.name.replace(/\.[^/.]+$/, '') + '.' + format;
          
          resolve({
            name: fileName,
            url: dataUrl,
            size: size,
            format: format
          });
        } else {
          reject(new Error('无法获取canvas上下文'));
        }
      };

      img.onerror = () => {
        reject(new Error('图片加载失败'));
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const handleConvert = async () => {
    if (selectedFiles.length === 0) {
      alert('请先选择图片文件');
      return;
    }

    setIsConverting(true);
    setConvertedImages([]);

    try {
      const converted = await Promise.all(
        selectedFiles.map(file => convertImage(file, targetFormat, quality))
      );
      setConvertedImages(converted);
    } catch (error) {
      console.error('转换失败:', error);
      alert('图片转换失败，请重试');
    } finally {
      setIsConverting(false);
    }
  };

  const downloadImage = (image: ConvertedImage) => {
    const link = document.createElement('a');
    link.href = image.url;
    link.download = image.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAll = () => {
    convertedImages.forEach(image => {
      setTimeout(() => downloadImage(image), 100);
    });
  };

  const clearAll = () => {
    setSelectedFiles([]);
    setConvertedImages([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="tool-container">
      <SEO 
        title="图片格式转换工具 - 在线图片格式转换器"
        description="免费在线图片格式转换工具，支持PNG、JPEG、WebP、BMP等格式互转。支持批量转换、质量调节、拖拽上传，操作简单快捷。"
        keywords="图片格式转换,图片转换器,PNG转JPG,JPEG转PNG,WebP转换,图片压缩,在线图片工具"
      />
      <h1 className="tool-title">图片格式转换工具</h1>
      <p className="tool-description">
        支持PNG、JPEG、WebP、BMP等格式之间的相互转换，可调节图片质量
      </p>

      {/* 文件上传区域 */}
      <div 
        className={`file-upload-area ${dragActive ? 'drag-active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <div className="upload-content">
          <div className="upload-icon">📁</div>
          <p>点击选择图片文件或拖拽到此处</p>
          <p className="upload-hint">支持 PNG、JPEG、WebP、BMP、GIF 格式</p>
        </div>
      </div>

      {/* 已选择的文件列表 */}
      {selectedFiles.length > 0 && (
        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <label className="form-label">已选择的文件 ({selectedFiles.length})</label>
            <button 
              className="btn btn-secondary"
              onClick={clearAll}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              清空所有
            </button>
          </div>
          <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '8px', padding: '10px' }}>
            {selectedFiles.map((file, index) => (
              <div key={index} style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                padding: '8px', 
                backgroundColor: '#f8f9fa', 
                marginBottom: '5px', 
                borderRadius: '4px' 
              }}>
                <div>
                  <span style={{ fontWeight: 'bold' }}>{file.name}</span>
                  <span style={{ color: '#666', marginLeft: '10px' }}>({formatFileSize(file.size)})</span>
                </div>
                <button 
                  className="btn btn-danger"
                  onClick={() => removeFile(index)}
                  style={{ fontSize: '12px', padding: '4px 8px' }}
                >
                  删除
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 转换设置 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div className="form-group">
          <label className="form-label">目标格式</label>
          <select 
            className="form-select"
            value={targetFormat}
            onChange={(e) => setTargetFormat(e.target.value as ImageFormat)}
          >
            <option value="png">PNG</option>
            <option value="jpeg">JPEG</option>
            <option value="webp">WebP</option>
            <option value="bmp">BMP</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">图片质量 ({Math.round(quality * 100)}%)</label>
          <input
            type="range"
            min="0.1"
            max="1"
            step="0.1"
            value={quality}
            onChange={(e) => setQuality(parseFloat(e.target.value))}
            className="form-range"
            disabled={targetFormat === 'png' || targetFormat === 'bmp'}
          />
          {(targetFormat === 'png' || targetFormat === 'bmp') && (
            <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              PNG和BMP格式不支持质量调节
            </p>
          )}
        </div>
      </div>

      {/* 转换按钮 */}
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <button 
          className="btn btn-primary"
          onClick={handleConvert}
          disabled={selectedFiles.length === 0 || isConverting}
          style={{ fontSize: '16px', padding: '12px 30px' }}
        >
          {isConverting ? '转换中...' : `转换为 ${targetFormat.toUpperCase()}`}
        </button>
      </div>

      {/* 转换结果 */}
      {convertedImages.length > 0 && (
        <div className="result-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 className="result-title">转换结果</h3>
            <button 
              className="btn btn-primary"
              onClick={downloadAll}
            >
              下载全部
            </button>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
            {convertedImages.map((image, index) => (
              <div key={index} style={{ 
                border: '1px solid #ddd', 
                borderRadius: '8px', 
                padding: '15px', 
                backgroundColor: 'white' 
              }}>
                <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                  <img 
                    src={image.url} 
                    alt={image.name}
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: '200px', 
                      objectFit: 'contain',
                      border: '1px solid #eee',
                      borderRadius: '4px'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>{image.name}</p>
                  <p style={{ color: '#666', fontSize: '14px' }}>
                    格式: {image.format.toUpperCase()} | 大小: {formatFileSize(image.size)}
                  </p>
                </div>
                <button 
                  className="btn btn-primary"
                  onClick={() => downloadImage(image)}
                  style={{ width: '100%' }}
                >
                  下载图片
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
        <h3 style={{ color: '#2c3e50', marginBottom: '15px' }}>功能说明</h3>
        <ul style={{ color: '#7f8c8d', lineHeight: '1.6' }}>
          <li><strong>多格式支持：</strong> 支持PNG、JPEG、WebP、BMP等主流图片格式</li>
          <li><strong>批量转换：</strong> 可同时选择多个图片进行批量转换</li>
          <li><strong>质量调节：</strong> 支持JPEG和WebP格式的质量调节</li>
          <li><strong>拖拽上传：</strong> 支持拖拽文件到页面进行上传</li>
          <li><strong>预览下载：</strong> 转换后可预览图片并单独或批量下载</li>
        </ul>
      </div>
    </div>
  );
};

export default ImageConverter;